import React, { useEffect } from 'react';
import { FormContext, useForm } from 'react-hook-form';
import { Grid, MenuItem, Tooltip, Typography } from '@material-ui/core';
import { DatePicker } from '@material-ui/pickers';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { startOfDay } from 'date-fns';
import { FormSelect, useDebouncedEffect } from '@lib/common';
import { FilterSection } from 'app/shared';
import { CorporateAccount } from 'app/state';

export interface BulkChangeBillingReservationFilters {
  corporateAccountId?: string;
  start?: Date;
  end?: Date;
}

interface Props {
  filters: BulkChangeBillingReservationFilters;
  corporateAccounts: CorporateAccount[];
  onChange: (filters: BulkChangeBillingReservationFilters) => void;
}

export const BulkChangeBillingReservationFilter: React.FC<Props> = ({
  filters,
  corporateAccounts,
  onChange,
}) => {
  const form = useForm<BulkChangeBillingReservationFilters>({
    defaultValues: {
      corporateAccountId: filters.corporateAccountId ?? '',
      start: filters.start,
      end: filters.end,
    },
  });

  useEffect(() => {
    form.register('corporateAccountId');
    form.register('start');
    form.register('end');
    // eslint-disable-next-line
  }, []);

  const { corporateAccountId, start, end } = form.watch({ nest: true });

  useDebouncedEffect(
    () => {
      onChange({
        corporateAccountId: corporateAccountId || undefined,
        start,
        end,
      });
    },
    300,
    [corporateAccountId, start, end]
  );

  return (
    <FilterSection>
      <FormContext {...form}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="h5" color="textSecondary" paragraph>
              Please select a corporate account and effective date range to find reservations to
              update.
            </Typography>
          </Grid>
          <Grid item xs={12} sm={4} md={3}>
            <FormSelect
              name="corporateAccountId"
              label="Corporate Account"
              defaultValue={filters.corporateAccountId ?? ''}
              fullWidth
              data-testid="bulkBillingCorporateAccountSelect"
              required={true}
            >
              <MenuItem value="">Please Select</MenuItem>
              {corporateAccounts.map(ca => (
                <MenuItem key={ca.id} value={ca.id}>
                  {ca.name}
                </MenuItem>
              ))}
            </FormSelect>
          </Grid>
          <Grid item xs={12} sm={4} md={3}>
            <Tooltip
              title="Only reservations that overlap this date range will be returned. The same range scopes the financial impact shown in the preview."
              placement="top"
              arrow
            >
              <span>
                <DatePicker
                  name="start"
                  label="Effective Start Date"
                  format="MM/dd/yyyy"
                  value={start ?? null}
                  maxDate={end ?? undefined}
                  error={!!form.errors.start}
                  onChange={value =>
                    form.setValue('start', (value && startOfDay(value)) ?? undefined)
                  }
                  color="primary"
                  InputProps={{ endAdornment: <FontAwesomeIcon icon="calendar-week" /> }}
                  helperText="First day to include when searching reservations."
                  size="small"
                  clearable
                  fullWidth
                  autoOk
                  data-testid="bulkBillingDateFrom"
                  required={true}
                />
              </span>
            </Tooltip>
          </Grid>
          <Grid item xs={12} sm={4} md={3}>
            <Tooltip
              title="Only reservations that overlap this date range will be returned. The same range scopes the financial impact shown in the preview."
              placement="top"
              arrow
            >
              <span>
                <DatePicker
                  name="end"
                  label="Effective End Date"
                  format="MM/dd/yyyy"
                  value={end ?? null}
                  minDate={start ?? undefined}
                  error={!!form.errors.end}
                  onChange={value =>
                    form.setValue('end', (value && startOfDay(value)) ?? undefined)
                  }
                  color="primary"
                  InputProps={{ endAdornment: <FontAwesomeIcon icon="calendar-week" /> }}
                  helperText="Last day to include when searching reservations."
                  size="small"
                  clearable
                  fullWidth
                  autoOk
                  data-testid="bulkBillingDateTo"
                  required={true}
                />
              </span>
            </Tooltip>
          </Grid>
        </Grid>
      </FormContext>
    </FilterSection>
  );
};
